import { useEffect, useState } from 'react';
import { FieldConfig } from '../../../../../components/CustomFormsComponents/CustomField';
import { useAppSelector } from '../../../../../hooks/reduxHooks';
import { getTodayDate } from '../../../../../utils/functions';
import { TEST_IDS } from '../testIds';

const SPECIAL_LICENCE_OPTIONS = [
    { id: 1, value: true, nombre: 'Si' },
    { id: 0, value: false, nombre: 'No' }
];

const useResponsiblesFieldsConfig = ({ t, formData, handleBlurValidation }: any) => {
    const [responsiblesFieldsConfig, setResponsiblesFieldsConfig] = useState<FieldConfig[]>([]);

    const carcinogenicParametricData = useAppSelector(
        (state) => state.carcinogenicReport.carcinogenicParametricData
    );

    useEffect(() => {
        const fieldsConfig: FieldConfig[] = [
            {
                type: 'text',
                inputType: 'number',
                label: t('radiologicalEquipmentForm.inputs.responsibles.cuit') as string,
                name: 'cuit',
                required: true,
                value: formData.cuit,
                disabled: false,
                maxLength: 11,
                minLength: 11,
                helperText: t('radiologicalEquipmentForm.inputs.responsibles.cuit') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12 },
                testID: TEST_IDS.newRadiologicalEquipment.responsiblesFormFields.cuit,
                validations: [{ name: 'required' }, { name: 'fixedLength', param: 11 }]
            },
            {
                type: 'text',
                label: t('radiologicalEquipmentForm.inputs.responsibles.responsibleName') as string,
                name: 'responsibleName',
                required: true,
                value: formData.responsibleName,
                disabled: false,
                helperText: t(
                    'radiologicalEquipmentForm.inputs.responsibles.responsibleName'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 6 },
                testID: TEST_IDS.newRadiologicalEquipment.responsiblesFormFields.responsibleName,
                validations: [{ name: 'required' }, { name: 'maxLength', param: 100 }, {name: "lettersOnly"}]
            },
            {
                type: 'text',
                label: t(
                    'radiologicalEquipmentForm.inputs.responsibles.responsibleSurname'
                ) as string,
                name: 'responsibleSurname',
                required: true,
                value: formData.responsibleSurname,
                disabled: false,
                helperText: t(
                    'radiologicalEquipmentForm.inputs.responsibles.responsibleSurname'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 6 },
                testID: TEST_IDS.newRadiologicalEquipment.responsiblesFormFields.responsibleSurname,
                validations: [{ name: 'required' }, { name: 'maxLength', param: 100 }, {name: "lettersOnly"}]
            },
            {
                type: 'text',
                label: t('radiologicalEquipmentForm.inputs.responsibles.permission') as string,
                name: 'permission',
                required: true,
                value: formData.permission,
                disabled: false,
                helperText: t('radiologicalEquipmentForm.inputs.responsibles.permission') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 6 },
                testID: TEST_IDS.newRadiologicalEquipment.responsiblesFormFields.permission,
                validations: [{ name: 'required' }, { name: 'maxLength', param: 100 }]
            },
            {
                type: 'text',
                label: t(
                    'radiologicalEquipmentForm.inputs.responsibles.professionalLicense'
                ) as string,
                name: 'professionalLicense',
                required: true,
                value: formData.professionalLicense,
                disabled: false,
                helperText: t(
                    'radiologicalEquipmentForm.inputs.responsibles.professionalLicense'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 6 },
                testID: TEST_IDS.newRadiologicalEquipment.responsiblesFormFields.professionalLicense,
                validations: [{ name: 'required' }, { name: 'maxLength', param: 50 }]
            },
            {
                type: 'date',
                label: t('radiologicalEquipmentForm.inputs.responsibles.expeditionDate') as string,
                name: 'expeditionDate',
                required: true,
                value: formData.expeditionDate,
                maxDate: getTodayDate(),
                disabled: false,
                helperText: t(
                    'radiologicalEquipmentForm.inputs.responsibles.expeditionDate'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 6 },
                testID: TEST_IDS.newRadiologicalEquipment.responsiblesFormFields.expeditionDate,
                validations: [{ name: 'required' }]
            },
            {
                type: 'date',
                label: t('radiologicalEquipmentForm.inputs.responsibles.expirationDate') as string,
                name: 'expirationDate',
                required: false,
                value: formData.expirationDate,
                minDate: formData.expeditionDate,
                disabled: !formData?.expeditionDate,
                helperText: t(
                    'radiologicalEquipmentForm.inputs.responsibles.expirationDate'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 6 },
                testID: TEST_IDS.newRadiologicalEquipment.responsiblesFormFields.expirationDate,
                validations: [{ name: 'required' }]
            },
            {
                type: 'autocomplete',
                label: t('radiologicalEquipmentForm.inputs.responsibles.responsibleType') as string,
                name: 'responsibleType',
                required: true,
                value: formData.responsibleType,
                disabled: false,
                helperText: t(
                    'radiologicalEquipmentForm.inputs.responsibles.responsibleType'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12 },
                options: carcinogenicParametricData?.tiposResponsableEquipo || [],
                getOptionLabel: (option) => option?.descripcion || '',
                testID: TEST_IDS.newRadiologicalEquipment.responsiblesFormFields.responsibleType,
                validations: [{ name: 'required' }]
            },
            {
                type: 'text',
                label: t(
                    'radiologicalEquipmentForm.inputs.responsibles.individualAuthorization'
                ) as string,
                name: 'individualAuthorization',
                required: true,
                value: formData.individualAuthorization,
                disabled: false,
                helperText: t(
                    'radiologicalEquipmentForm.inputs.responsibles.individualAuthorization'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12 },
                testID: TEST_IDS.newRadiologicalEquipment.responsiblesFormFields.individualAuthorization,
                validations: [{ name: 'required' }, { name: 'maxLength', param: 50 }]
            },
            {
                type: 'autocomplete-default',
                label: t('radiologicalEquipmentForm.inputs.responsibles.specialLicense') as string,
                name: 'specialLicense',
                required: true,
                value: formData.specialLicense,
                disabled: false,
                helperText: t(
                    'radiologicalEquipmentForm.inputs.responsibles.specialLicense'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12 },
                options: SPECIAL_LICENCE_OPTIONS,
                testID: TEST_IDS.newRadiologicalEquipment.responsiblesFormFields.specialLicense,
                validations: [{ name: 'required' }]
            }
        ];

        if (formData.responsibleType === 1) {
            fieldsConfig.push({
                type: 'text',
                label: t(
                    'radiologicalEquipmentForm.inputs.responsibles.habilitationAddress'
                ) as string,
                name: 'habilitationAddress',
                required: true,
                value: formData.habilitationAddress,
                disabled: false,
                helperText: t(
                    'radiologicalEquipmentForm.inputs.responsibles.habilitationAddress'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12 },
                testID: TEST_IDS.newRadiologicalEquipment.responsiblesFormFields.habilitationAddress,
                validations: [{ name: 'required' }, { name: 'maxLength', param: 50 }]
            });
        }

        if (formData.responsibleType === 3) {
            fieldsConfig.push({
                type: 'text',
                label: t(
                    'radiologicalEquipmentForm.inputs.responsibles.professionalDegree'
                ) as string,
                name: 'professionalDegree',
                required: true,
                value: formData.professionalDegree,
                disabled: false,
                helperText: t(
                    'radiologicalEquipmentForm.inputs.responsibles.professionalDegree'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12 },
                testID: TEST_IDS.newRadiologicalEquipment.responsiblesFormFields.professionalDegree,
                validations: [{ name: 'required' }, { name: 'maxLength', param: 50 }]
            });
        }

        setResponsiblesFieldsConfig(fieldsConfig);
    }, [t, formData?.responsibleType, formData?.expeditionDate]);

    return { responsiblesFieldsConfig };
};

export default useResponsiblesFieldsConfig;